import { LotI, NFTI, StandardTypeE } from '@tacans/shared-marketplace-layer';
import { useMemo } from 'react';
import { useActiveLots } from 'src/features/common/collectible/hooks/collectible.hooks';
import { useAvailableCopies } from 'src/features/common/collectible/hooks/use-available-copies';
import { useIsActiveNftOwner } from 'src/features/common/collectible/hooks/use-is-active-nft-owner';
import { useActiveOwners } from 'src/features/common/collectible/hooks/use-nft-owners';
import { useMyWalletsById } from 'src/features/common/wallet/hooks/use-my-wallets-by-id';
import { getNftTypeByStandard } from 'src/utils/lot.utils';

interface ArgsType {
    nft: NFTI;
    lotToDisplay?: LotI;
}

interface ReturnType {
    mint: boolean;
    mintAndSell: boolean;
    download: boolean;
    createLot: boolean;
    transfer: boolean;
    burn: boolean;
    delete: boolean;
    share: boolean;
    contactSeller: boolean;
    deleteSingle: boolean;
}

// Status flow
// -> mint (or mind and sell as single operation)
// -> (transfer, resale, lot actions - sell, change price, unlist from sell)
// -> burn (delete from blockchain)
// -> delete (from website)
export const useNftAvailableActions = ({ nft, lotToDisplay }: ArgsType): ReturnType => {
    const isActiveNftOwner = useIsActiveNftOwner();
    const activeOwners = useActiveOwners();
    const activeLots = useActiveLots();
    const availableCopies = useAvailableCopies();
    const myWallets = useMyWalletsById();
    const nftCopies = Number(nft.copies!);
    const { isMultiple } = getNftTypeByStandard(nft?.standard!);

    const hasOneCopy = nftCopies === 1;

    return useMemo(
        () => ({
            mint: isActiveNftOwner && !nft.isPublished,
            mintAndSell: isActiveNftOwner && !nft.isPublished && nft.standard === StandardTypeE.SINGLE,
            createLot:
                availableCopies > 0 &&
                !!nft.isPublished &&
                ((hasOneCopy && !activeLots.length && !lotToDisplay) || !hasOneCopy),
            transfer: isActiveNftOwner && !!nft.isPublished && availableCopies > 0,
            burn: isActiveNftOwner && !!nft.isPublished && availableCopies > 0,
            delete: (isMultiple && !isActiveNftOwner && !!myWallets[nft?.owner] && !activeOwners) || (isMultiple && !nft?.isPublished),
            deleteSingle: (!isMultiple && !isActiveNftOwner && !!myWallets[nft?.owner]) || (!isMultiple && !nft?.isPublished),
            download: isActiveNftOwner && !!+(nft?.copies ?? 0),
            share: !!nft.isPublished && !!+(nft?.copies ?? 0),
            contactSeller: !!nftCopies && (!isActiveNftOwner || (isActiveNftOwner && activeOwners.totalCount > 1)),
        }),
        [
            lotToDisplay,
            nftCopies,
            myWallets,
            activeLots?.length,
            activeOwners?.totalCount,
            availableCopies,
            hasOneCopy,
            isActiveNftOwner,
            nft,
        ]
    );
};
